// Datenmodell und reine Logik des Dokumente-Explorers.
//
// Bewusst frei von React, Fetch und Router: der FileExplorer rendert nur,
// gefüttert wird er von zwei Adaptern (Document-API bzw. Kundenakten-Index).
// Alles hier ist damit direkt testbar (explorerModel.test.ts).

import { normalizeRelPath, splitPath } from "../../../utils/fileUtils";

/** Ein Unterordner auf einer Ebene. `path` endet IMMER auf "/". */
export type ExplorerFolder = {
  kind: "folder";
  /** Segmentname (das, was angezeigt wird). */
  name: string;
  /** Vollständiger Pfad ab der Wurzel, auf "/" endend. */
  path: string;
  /** Anzahl Dateien darunter — REKURSIV, inkl. Unterordner. */
  count?: number;
  /**
   * Feste Position vor der alphabetischen Ordnung (kleiner = weiter oben).
   * Der KMVS-Ordner steht so immer zuerst, seine Bereiche in der Reihenfolge,
   * die der PO vorgegeben hat. Ohne Angabe: alphabetisch hinter allen festen.
   */
  reihenfolge?: number;
  /**
   * Ordner, den es nur in der Anzeige gibt (KMVS-Teilbaum). Er lässt sich
   * öffnen, aber nicht umbenennen, verschieben oder befüllen.
   */
  virtuell?: boolean;
};

/** Eine Datei auf einer Ebene bzw. ein Suchtreffer. */
export type ExplorerFile<P = unknown> = {
  kind: "file";
  /** Stabiler Schlüssel (Dokument-Id bzw. Index-Id). */
  key: string;
  /** Basename inkl. Extension. */
  name: string;
  /** Verzeichnisteil OHNE abschließenden "/" ("" = Wurzel). */
  dir: string;
  size?: number | string | null;
  date?: string | null;
  mimeType?: string | null;
  /** Volltext-Fundstelle mit [[…]]-Markern. */
  snippet?: string | null;
  category?: string | null;
  /** Gedimmt darstellen (z. B. Schaden-Dateien, die der Import ausschließt). */
  dimmed?: boolean;
  /**
   * Papierkorb (PO 16.09.2026): Zeitpunkt des Löschens, sonst null/fehlend.
   * Steuert im Menü, ob „Wiederherstellen" statt „Löschen" angeboten wird.
   */
  deleted?: string | null;
  /** Zusatz-Kennzeichnung rechts vom Namen. */
  tag?: string | null;
  /** Tooltip der Kennzeichnung (z. B. der Vorgangstitel hinter „aus Vorgang …"). */
  tagTitle?: string | null;
  /** Nutzlast des Adapters (DocumentDto bzw. KundenaktenFile). */
  payload?: P;
};

/** Virtuelle, stets aufgeklappte Dateigruppe auf einer Explorer-Ebene. */
export type ExplorerGroup<P = unknown> = {
  kind: "group";
  key: string;
  name: string;
  count?: number;
  files: ExplorerFile<P>[];
};

export type ExplorerRow<P = unknown> = ExplorerFolder | ExplorerGroup<P> | ExplorerFile<P>;

/** Zeilen-Schlüssel für DataTable und Auswahl. Ordner und Dateien sind
 *  getrennte Namensräume — ein Ordner „12" und eine Datei mit Id 12 dürfen
 *  sich nicht überschneiden. */
export const rowKeyOf = (r: ExplorerRow): string => {
  if (r.kind === "folder") return `d:${r.path}`;
  if (r.kind === "group") return `g:${r.key}`;
  return `f:${r.key}`;
};

/** Normalisiert einen Ordnerpfad: "" für die Wurzel, sonst auf "/" endend. */
export const normalizeFolderPath = (path: string): string => {
  const p = normalizeRelPath(path);
  if (!p) return "";
  return p.endsWith("/") ? p : p + "/";
};

/** Elternpfad: "A/B/" → "A/", "A/" → "", "" → "". */
export const parentPath = (path: string): string => {
  const p = normalizeFolderPath(path);
  if (!p) return "";
  const withoutTrailing = p.slice(0, -1);
  const i = withoutTrailing.lastIndexOf("/");
  return i < 0 ? "" : withoutTrailing.slice(0, i + 1);
};

/** Pfadsegmente eines Ordnerpfads ("Verträge/2019/" → ["Verträge","2019"]). */
export const folderSegments = (path: string): string[] =>
  normalizeFolderPath(path).split("/").filter(Boolean);

/**
 * Namensvergleich in Explorer-Manier: alphabetisch, aber NUMERISCH — „Datei2"
 * vor „Datei10" — und ohne Groß-/Kleinschreibungs-Vorrang. Das ist die
 * clientseitige Sortierung; die serverseitige (ORDER BY fileName) kann das
 * nicht leisten und weicht bei gemischten Zahlen ab.
 */
export const compareNames = (a: string, b: string): number =>
  a.localeCompare(b, "de", { numeric: true, sensitivity: "base" });

/** Ordner-Reihenfolge im Explorer: feste Positionen zuerst, dann nach Namen. */
export const compareFolders = (a: ExplorerFolder, b: ExplorerFolder): number => {
  const ra = a.reihenfolge ?? Number.POSITIVE_INFINITY;
  const rb = b.reihenfolge ?? Number.POSITIVE_INFINITY;
  if (ra !== rb) return ra < rb ? -1 : 1;
  return compareNames(a.name, b.name);
};

/**
 * Ergänzt die Ordner einer Ebene um die selbst angelegten (noch leeren) Ordner.
 *
 * `angelegt` sind vollständige Pfade (`Verträge/2026/`); auf der Ebene
 * `Verträge/` wird daraus der Unterordner „2026". Liegt ein angelegter Ordner
 * tiefer (`A/B/C/`), erscheinen auch die Zwischenebenen — sonst wäre er nicht
 * erreichbar. Bereits bekannte Ordner bleiben unverändert (mit Zähler).
 */
export const mitAngelegtenOrdnern = (
  vorhanden: readonly ExplorerFolder[],
  angelegt: readonly string[],
  ebene: string,
): ExplorerFolder[] => {
  const basis = normalizeFolderPath(ebene);
  const namen = new Set(vorhanden.map((f) => f.name));
  const dazu: ExplorerFolder[] = [];
  for (const roh of angelegt) {
    const pfad = normalizeFolderPath(roh);
    if (!pfad.startsWith(basis) || pfad === basis) continue;
    const name = pfad.slice(basis.length).split("/")[0];
    if (!name || namen.has(name)) continue;
    namen.add(name);
    dazu.push({ kind: "folder", name, path: basis + name + "/", count: 0 });
  }
  return dazu.length ? [...vorhanden, ...dazu] : [...vorhanden];
};

/** Liegt `ziel` in `ordner` oder ist es derselbe Ordner? (Kein Ordner in sich selbst.) */
export const liegtIn = (ziel: string, ordner: string): boolean =>
  normalizeFolderPath(ziel).startsWith(normalizeFolderPath(ordner));

/**
 * Freier Name in einer Ebene: „Neuer Ordner", „Neuer Ordner (2)", … bzw.
 * „Neues Dokument.docx", „Neues Dokument (2).docx" — wie Windows.
 */
export const freierName = (wunsch: string, vorhanden: Iterable<string>, endung = ""): string => {
  const belegt = new Set([...vorhanden].map((n) => n.toLocaleLowerCase("de")));
  const kandidat = (n: number) => (n === 1 ? `${wunsch}${endung}` : `${wunsch} (${n})${endung}`);
  let n = 1;
  while (belegt.has(kandidat(n).toLocaleLowerCase("de"))) n += 1;
  return kandidat(n);
};

/**
 * Zusammenhängender Bereich zwischen zwei Zeilen-Schlüsseln (Shift-Auswahl).
 * Unbekannte Schlüssel ⇒ nur das Ziel, damit ein Shift-Klick nach einem
 * Ordnerwechsel nicht die halbe Liste markiert.
 */
export const rangeBetween = (keys: string[], anchor: string, target: string): string[] => {
  const a = keys.indexOf(anchor);
  const b = keys.indexOf(target);
  if (a < 0 || b < 0) return b >= 0 ? [keys[b]] : [];
  const [from, to] = a <= b ? [a, b] : [b, a];
  return keys.slice(from, to + 1);
};

/* ---------------- Baum aus flachen Pfaden ---------------- */

/** Eingabe für den In-Memory-Baum (Kundenakten-Index). */
export type FlatPathEntry = {
  id: number | string;
  /** Pfad relativ zur Wurzel, inkl. Unterordner. */
  relPath: string;
  fileName?: string;
  sizeBytes?: number | string | null;
  isSchaden?: boolean;
};

export type ExplorerTree = {
  /** Unterordner je Ebene (Schlüssel: "" für die Wurzel, sonst auf "/" endend). */
  folders: Map<string, ExplorerFolder[]>;
  /** Dateien DIREKT auf dieser Ebene. */
  files: Map<string, ExplorerFile<FlatPathEntry>[]>;
};

/**
 * Baut aus flachen Pfaden (`KundenaktenFile.relPath`) einen vollständigen
 * In-Memory-Baum. Der Endpunkt liefert die Dateiliste eines Ordners ungepaged,
 * die Daten liegen also ohnehin komplett im Client — ein eigener
 * Backend-Endpunkt für die Ordner-Aggregation wäre reine Zusatzarbeit.
 *
 * Robust gegen: Windows-Backslashes, doppelte und führende Slashes, Dateien
 * direkt in der Wurzel, sowie Einträge mit leerem Pfad (werden übersprungen).
 * Ordner-`count` ist REKURSIV — wie beim Server-Pendant /documents/folders.
 */
export const buildTreeFromRelPaths = (entries: FlatPathEntry[]): ExplorerTree => {
  const folders = new Map<string, ExplorerFolder[]>();
  const files = new Map<string, ExplorerFile<FlatPathEntry>[]>();
  // Ordnerpfad → rekursive Dateizahl
  const counts = new Map<string, number>();
  // Ordnerpfad → Set der direkten Unterordnernamen (Dedup vor dem Sortieren)
  const childNames = new Map<string, Set<string>>();

  const ensureLevel = (path: string) => {
    if (!childNames.has(path)) childNames.set(path, new Set());
    if (!files.has(path)) files.set(path, []);
  };
  ensureLevel("");

  for (const e of entries) {
    const clean = normalizeRelPath(e.relPath ?? "");
    // Leere UND rein aus Leerzeichen bestehende Segmente fliegen raus — sonst
    // entstünde aus einer defekten Index-Zeile ein Geisterordner ohne Namen.
    const segments = clean.split("/").filter((s) => s.trim().length > 0);
    if (segments.length === 0) continue;

    const base = segments[segments.length - 1];
    const dirSegments = segments.slice(0, -1);

    // Ordnerkette anlegen und rekursive Zähler hochziehen.
    let prefix = "";
    for (const seg of dirSegments) {
      ensureLevel(prefix);
      childNames.get(prefix)!.add(seg);
      const childPath = prefix + seg + "/";
      counts.set(childPath, (counts.get(childPath) ?? 0) + 1);
      prefix = childPath;
    }
    ensureLevel(prefix);

    files.get(prefix)!.push({
      kind: "file",
      key: String(e.id),
      name: e.fileName || base,
      dir: prefix ? prefix.slice(0, -1) : "",
      size: e.sizeBytes ?? null,
      dimmed: !!e.isSchaden,
      tag: e.isSchaden ? "Schaden" : null,
      payload: e,
    });
  }

  for (const [path, names] of childNames) {
    folders.set(
      path,
      [...names]
        .sort(compareNames)
        .map((name) => ({
          kind: "folder" as const,
          name,
          path: path + name + "/",
          count: counts.get(path + name + "/") ?? 0,
        })),
    );
  }
  for (const list of files.values()) list.sort((a, b) => compareNames(a.name, b.name));

  return { folders, files };
};

/** Unterordner einer Ebene (leeres Array, wenn die Ebene unbekannt ist). */
export const foldersAt = (tree: ExplorerTree, path: string): ExplorerFolder[] =>
  tree.folders.get(normalizeFolderPath(path)) ?? [];

/** Dateien direkt auf einer Ebene. */
export const filesAt = (tree: ExplorerTree, path: string): ExplorerFile<FlatPathEntry>[] =>
  tree.files.get(normalizeFolderPath(path)) ?? [];

/** Alle Dateien ab einer Ebene abwärts — für die Suche im read-only-Baum. */
export const filesBelow = (tree: ExplorerTree, path: string): ExplorerFile<FlatPathEntry>[] => {
  const root = normalizeFolderPath(path);
  const out: ExplorerFile<FlatPathEntry>[] = [];
  for (const [levelPath, list] of tree.files) {
    if (levelPath.startsWith(root)) out.push(...list);
  }
  return out.sort((a, b) => compareNames(a.dir + "/" + a.name, b.dir + "/" + b.name));
};

/**
 * Zerlegt einen gespeicherten Dokument-`fileName` (der bei Legacy-Importen den
 * ordner-relativen Pfad enthält) in die Explorer-Darstellung.
 */
export const fileFromStoredName = (storedName: string): { dir: string; base: string } =>
  splitPath(normalizeRelPath(storedName));
